<template>
  <section class="activity-info-section">
    <activity-active-disable-modal
      :visible.sync="visibleActiveDisableModal"
      :associate="associate"
      :associate-id="associateId"
    />

    <activity-separated-disable-modal :visible.sync="visibleSeparatedDisableModal" :associate-id="associateId" />

    <section-divider title="Activity">
      <template v-if="hasActivityActions" slot="actions">
        <a-button
          v-if="canManageAssociate && (associate.termination || !associate.is_active)"
          icon="login"
          @click="openActivityConfirm(true)"
        >
          {{ activationActionLabel }}
        </a-button>
        <a-button
          v-if="
            canManageAssociate &&
            associate.termination_job &&
            !associate.termination &&
            associate.payment_info?.type !== 'Contractor'
          "
          icon="close-circle"
          @click="openFutureTerminationConfirm"
        >
          Cancel separation
        </a-button>
        <a-button v-if="canManageAssociate && associate.is_active" icon="logout" @click="openActivityConfirm(false)">
          Disable
        </a-button>
        <a-button v-if="canManageAssociate && associate.termination" icon="edit" @click="visibleSeparationModal = true">
          Edit Separation
        </a-button>
        <a-button
          v-if="canManageAssociate && !associate.termination"
          type="danger"
          ghost
          icon="disconnect"
          @click="visibleSeparationModal = true"
        >
          Separate
        </a-button>
        <a-tooltip v-if="canDeleteAssociate" title="Delete associate">
          <a-button
            type="danger"
            ghost
            icon="delete"
            aria-label="Delete associate"
            data-testid="profile-view-job-delete-associate-menu-item"
            @click="openDeleteAssociateConfirm"
          >
            Delete
          </a-button>
        </a-tooltip>
      </template>
    </section-divider>

    <div v-if="loadingAssociate" class="loading-spin">
      <a-spin />
    </div>
    <div v-else class="activity-info-grid">
      <template v-if="context">
        <div v-if="context.associate" class="activity-info-field">
          <span>Profile Creator</span>
          <grouped-associate-info
            :first-name="context.associate.first_name"
            :last-name="context.associate.last_name"
            :associate-number="context.associate.associate_number"
          />
        </div>
        <div class="activity-info-field">
          <span>Creation Source</span>
          <strong>{{ context.origin }}</strong>
        </div>
        <div class="activity-info-field">
          <span>Profile Created At</span>
          <strong>{{ formatDateString(context.created_at) }}</strong>
        </div>
      </template>

      <div class="activity-info-field">
        <span>First Shift Date</span>
        <strong>{{ associate?.first_shift_date ? formatDateString(associate.first_shift_date) : '-' }}</strong>
      </div>

      <div class="activity-info-field">
        <span>Hire Date</span>
        <strong>{{ associate?.hire_date ? formatDateString(associate.hire_date) : '-' }}</strong>
      </div>

      <div class="activity-info-field">
        <span>Seniority Date</span>
        <strong>{{ associate?.seniority_date ? formatDateString(associate.seniority_date) : '-' }}</strong>
      </div>

      <div v-if="isFutureTermination" class="activity-info-field">
        <span>Future Separation</span>
        <strong>{{ associate.termination_job ? formatDateString(associate.resigned_date) : '-' }}</strong>
      </div>
    </div>

    <termination-info v-if="associate.termination || visibleSeparationModal" :visible.sync="visibleSeparationModal" />
  </section>
</template>

<script>
import GroupedAssociateInfo from '@/components/ui/grouped-associate-info.vue'
import ActivityActiveDisableModal from './activity-active-disable-modal.vue'
import ActivitySeparatedDisableModal from './activity-separated-disable-modal.vue'
import ProfileTerminationInfo from './termination.vue'
import util from '@/util'
import { Button, Modal, notification, Spin, Tooltip } from 'ant-design-vue'
import moment from 'moment-timezone'
import { createNamespacedHelpers } from 'vuex'
import permissionMixin from '@/mixins/permissionMixin'
import { getMinimumPayActivationFields, getMinimumPayActivationMessage } from '@/modules/profile/utils/minimum-pay'
import SectionDivider from '@/components/ui/section-divider.vue'

const { mapActions: profileActions, mapState: profileState } = createNamespacedHelpers('profile')
const { mapActions: modalActions } = createNamespacedHelpers('modals')

export default {
  components: {
    'a-button': Button,
    'a-spin': Spin,
    'a-tooltip': Tooltip,
    'activity-active-disable-modal': ActivityActiveDisableModal,
    'activity-separated-disable-modal': ActivitySeparatedDisableModal,
    'grouped-associate-info': GroupedAssociateInfo,
    'section-divider': SectionDivider,
    'termination-info': ProfileTerminationInfo
  },
  mixins: [permissionMixin],
  props: {
    associateId: {
      type: Number,
      default: null
    }
  },
  data() {
    return {
      loadingAssociate: false,
      visibleActiveDisableModal: false,
      visibleSeparatedDisableModal: false,
      visibleSeparationModal: false,
      context: undefined
    }
  },
  computed: {
    ...profileState({
      associate: state => state.associate
    }),

    currentProjectId() {
      return this.$store.state.applicationState.currentProject.id
    },

    canManageAssociate() {
      return this.hasUncontrolledPermission('manage_associates', {
        id: this.associate.id
      })
    },

    canDeleteAssociate() {
      return this.associate.project_id === this.currentProjectId && this.canManageAssociate
    },

    hasActivityActions() {
      return this.canManageAssociate || this.canDeleteAssociate
    },

    associateName() {
      return [this.associate?.first_name, this.associate?.last_name].filter(Boolean).join(' ')
    },

    activationActionLabel() {
      if (!this.associate.termination) {
        return 'Activate'
      }

      return this.associate.payment_info?.type === 'Contractor' ? 'Reactivate' : 'Rehire'
    },

    isFutureTermination() {
      return (
        this.associate?.termination_job && moment().utc().startOf('day').isSameOrBefore(this.associate?.resigned_date)
      )
    },

    isDnrTermination() {
      return this.associate?.termination?.type === 'dnr'
    },

    shouldSeparateBeforeDisable() {
      return this.associate?.work_status === 'active' && !this.associate?.termination
    },

    minimumPayActivationFields() {
      return getMinimumPayActivationFields(
        this.associate?.payment_info,
        this.$store.state.applicationState.currentProject?.payment_settings
      )
    },

    minimumPayActivationMessage() {
      return getMinimumPayActivationMessage(this.minimumPayActivationFields)
    }
  },
  watch: {
    associateId: {
      handler(newVal) {
        if (newVal) {
          this.fetchAssociate()
        }
      }
    }
  },
  methods: {
    ...modalActions(['openModal']),
    ...profileActions([
      'deleteAssociate',
      'patchAssociateActivate',
      'getCreationContext',
      'reloadAssociate',
      'postCancelFutureTermination'
    ]),

    formatDateString(dateString) {
      return moment(dateString).utc().format('MM/DD/YYYY')
    },

    showNotification(type, message, description) {
      notification[type]({
        message,
        description
      })
    },

    openActivityConfirm(checked) {
      if (checked && this.minimumPayActivationFields.length) {
        this.showNotification('warning', 'Update pay value first', this.minimumPayActivationMessage)
        return
      }

      if (!checked) {
        if (this.shouldSeparateBeforeDisable) {
          this.visibleActiveDisableModal = true
        } else {
          this.visibleSeparatedDisableModal = true
        }

        return
      }

      Modal.confirm({
        title: 'Are you sure?',
        content: this.getActivityConfirmContent(),
        onOk: async () => {
          await this.setActivity()
        },
        onCancel() {}
      })
    },

    getActivityConfirmContent() {
      if (this.associate.termination) {
        return this.associate.payment_info.type === 'Contractor'
          ? this.getContractorReactivateConfirmContent()
          : 'Are you sure you want to rehire Employee? Make sure they recomplete onboarding before returning to work!'
      }

      return 'You are about to activate an associate. If the associate has a separation, it will be canceled. If you are sure - click OK.'
    },

    getContractorReactivateConfirmContent() {
      return this.isDnrTermination
        ? 'Are you sure you want to reactivate Associate? This will remove the Associate from the DNR list, allowing them to resume work.'
        : 'Are you sure you want to reactivate Associate? This will cancel the separation, allowing them to resume work.'
    },

    openFutureTerminationConfirm() {
      Modal.confirm({
        title: 'Are you sure?',
        content: 'You are about to canceling the future separation. If you are sure - click OK.',
        onOk: async () => {
          await this.cancelFutureTermination()
        },
        onCancel() {}
      })
    },

    async cancelFutureTermination() {
      try {
        await this.postCancelFutureTermination({
          associate_id: this.associateId,
          job_id: this.associate.termination_job
        })

        this.showNotification('success', 'Activation Set', 'Future separation has been successfully canceled')
      } catch (error) {
        this.showNotification('error', 'Future Separation Error', error)
      }
    },

    async setActivity() {
      try {
        await this.patchAssociateActivate({
          associate_id: this.associateId
        })

        this.showNotification('success', 'Activation Set', 'Associate has been successfully activated')
        this.reloadAssociate(true)
      } catch (error) {
        this.showNotification('error', 'Activation Set Error', error)
      }
    },

    openDeleteAssociateConfirm() {
      const h = this.$createElement

      Modal.confirm({
        title: 'Delete this associate?',
        content: h('div', [
          h('p', [
            "You're about to delete ",
            h('strong', this.associateName),
            " from the company. If this is the person's last associate, their user account will be deleted too."
          ]),
          h('p', [
            "This will remove the associate's profile, their login access, and their associated records. This action cannot be undone."
          ]),
          h('p', "Only continue if you're sure this deletion is intended.")
        ]),
        okText: 'Continue',
        okType: 'danger',
        cancelText: 'Cancel',
        onOk: () => {
          this.openDeleteAssociateModal()
        },
        onCancel() {}
      })
    },

    openDeleteAssociateModal() {
      const description = `This is permanent and can't be recovered. ${this.associateName} will be deleted. If this is the person's last associate, their user account will be deleted too.`

      this.openModal({
        type: 'deletion',
        data: {
          visible: true,
          title: 'Confirm deletion',
          description,
          confirmText: `To confirm, type the associate's name ${this.associateName} below.`,
          confirmationValue: this.associateName,
          inputPlaceholder: this.associateName,
          helperText: `Type "${this.associateName}" to enable the button.`,
          confirmButtonText: 'Delete associate',
          onConfirm: this.deleteAssociateConfirmed
        }
      })
    },

    async deleteAssociateConfirmed() {
      const deletedAssociate = this.associate

      try {
        await this.deleteAssociate({
          associate_id: deletedAssociate.id
        })

        this.showNotification(
          'success',
          'Success',
          'Associate has been deleted successfully. The page will reload automatically in 5 seconds.'
        )

        setTimeout(() => {
          window.location.reload()
        }, 5000)
      } catch (error) {
        this.showNotification('error', 'Error', typeof error === 'string' ? error : error?.message)
      }
    },

    async fetchAssociate() {
      this.loadingAssociate = true

      try {
        const creationContextResponse = await this.getCreationContext({
          associate_id: this.associateId
        })
        const { origin, ...otherData } = creationContextResponse

        this.context = { ...otherData, origin: util.capitalize(origin) }
      } catch (error) {
        this.showNotification('error', 'Error', typeof error === 'string' ? error : error?.message)
      } finally {
        this.loadingAssociate = false
      }
    }
  },
  mounted() {
    if (this.associateId) {
      this.fetchAssociate()
    }
  }
}
</script>

<style scoped>
.activity-info-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.activity-info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 8px;
}

.activity-info-field {
  min-width: 0;
}

.activity-info-field > span {
  display: block;
  margin-bottom: 4px;
  color: var(--text-color-secondary);
  font-size: 12px;
}

.activity-info-field > strong {
  display: block;
  overflow-wrap: anywhere;
}
</style>
